/**
 * CRUISE-UX-DATA-001 · Canonical PhoneDisplay component.
 *
 * ONE OWNER → MANY CONSUMERS.
 *
 * Renders a stored phone value with international formatting and a
 * safe `tel:` link. Never invents dialing digits — if the incoming
 * value is not a canonical E.164 string, we render the raw value as
 * fallback and skip the tel: link so we never construct a broken
 * international dial string at render time.
 *
 * Props:
 *   value  : string  — canonical E.164 (e.g. "+14165550100"). Free-text
 *                       legacy values are rendered as-is (no tel: link).
 *   ext    : string? — optional extension token, shown after the number.
 *   ariaLabel : optional accessible label
 *   className : Tailwind extras
 */
import React from "react";
import { parsePhoneNumber } from "libphonenumber-js/min";

export function formatDisplay(e164) {
  if (!e164 || typeof e164 !== "string") return "";
  if (!e164.startsWith("+")) return e164;                    // legacy passthrough
  try {
    const p = parsePhoneNumber(e164);
    if (!p || !p.isValid()) return e164;
    return p.formatInternational();
  } catch {
    return e164;
  }
}

export function isE164(v) {
  if (!v || typeof v !== "string" || !v.startsWith("+")) return false;
  try {
    const p = parsePhoneNumber(v);
    return !!p && p.isValid();
  } catch {
    return false;
  }
}

export default function PhoneDisplay({ value, ext, ariaLabel, className }) {
  if (!value) return null;
  const dialable = isE164(value);
  const display = formatDisplay(value) + (ext ? ` ext. ${ext}` : "");
  const cls = className || "text-[13px] text-slate-800 tabular-nums";
  if (dialable) {
    // tel: URIs require RFC 3966 form. E.164 already qualifies.
    const href = ext ? `tel:${value};ext=${ext}` : `tel:${value}`;
    return (
      <a
        href={href}
        className={`${cls} hover:underline`}
        aria-label={ariaLabel || `Call ${display}`}
        data-testid="cc-phone-display"
      >
        {display}
      </a>
    );
  }
  return (
    <span className={cls} data-testid="cc-phone-display">
      {display}
    </span>
  );
}
